import useAxios from "src/core/shared/hooks/useAxios";
import {
  RequestConfig,
  UseApiRequest,
  EndpointConfig
} from "src/core/shared/api/config.api";
import {
  PaginatedParams,
  FilterParams,
  PaginatedData
} from "src/core/shared/api";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  IError,
  IListErrorData
} from "src/core/errors/api/errors.api";
import { ISchedule } from "src/core/schedules/api/schedules.api";
import { IProduct } from "src/core/products/api/products.api";
import {
  IOrder,
  IOrderVariant
} from "src/core/orders/api/orders.api";

/**
 * Types
 */
export type ListListsParams = { pagination: PaginatedParams<IList>; filters: FilterParams<IList> };
export type DropListParams = { pagination: PaginatedParams<IList>; filters: FilterParams<Pick<IList, "createdAt" | "updatedAt" | "name">> };
export type GetDropListsParams = { dropId: number; params: DropListParams };
export type CreateListParams = Pick<IList, "branchId" | "name" | "listLines"> & { orderVariantId?: number };
export type CreateListFromOrderParams = { listName: string; id: number };
export type CreateOrderFromListParams = { id: number; deliveryDate: Date; editBeforePlacing: boolean; customerReference?: string };
export type SubmitListParams = IList;
export type UpdateListParams = Partial<IList> & Pick<IList, "id">;
export type GetUserAccessibleListsParams = { params: DropListParams };

export type IList = {
  id: number;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  branchId: number;
  orderVariantId: number;
  listLines: IListLine[];
  listNotes: IListNote[];
  branch: IDrop;
  errors?: Array<IError<IListErrorData>>;
  customerReference?: string;
  schedule?: ISchedule;
  deleted: boolean;
  hasDetachedOrders?: boolean;
  orderVariant?: IOrderVariant;
};

export type IListKeys = Omit<IList, "listLines" | "schedule">;

export interface IListLine {
  id?: number;
  listId?: number;
  price: number | null;
  totalPrice?: number | null;
  productId: number;
  product: IProduct;
  quantity: number;
  totalVatValue: number | null;
  vatPercentage: number | null;
}

export interface IListNote {
  id?: number;
  orderId: number;
  body: string;
  authorId: number;
  source: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ListPreRequestUpdate {
  toUpdate: number;
}

/**
 * Endpoint Config
 */
type ListsEndpointConfig = {
  CREATE_ORDER_FROM_LIST: EndpointConfig<CreateOrderFromListParams, IOrder>;
  CREATE_LIST_FROM_ORDER: EndpointConfig<CreateListFromOrderParams, IList>;
  GET_DROP_LISTS: EndpointConfig<GetDropListsParams, PaginatedData<IList>>;
  GET_USER_ACCESSIBLE_LISTS: EndpointConfig<GetUserAccessibleListsParams, PaginatedData<IList>>;
  GET_LIST: EndpointConfig<{ listId: number }, IList>;
  GET_LISTS: EndpointConfig<ListListsParams, PaginatedData<IList>>;
  PRE_UPDATE_LIST: EndpointConfig<{ listId: number }, ListPreRequestUpdate>;
  CREATE_LIST: EndpointConfig<CreateListParams, IList>;
  UPDATE_LIST: EndpointConfig<UpdateListParams, IList>;
  DELETE_LIST: EndpointConfig<{ listId: number }, { message: string }>;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/lists`;

/**
 * API Requests
 */
const requests: RequestConfig<ListsEndpointConfig> = {
  CREATE_ORDER_FROM_LIST: ({
    id, deliveryDate, customerReference, editBeforePlacing
  }) => ({
    url: `${BASE_URL}/${id}/to-order`,
    method: "POST",
    data: {
      deliveryDate,
      customerReference,
      editBeforePlacing
    }
  }),

  CREATE_LIST_FROM_ORDER: ({ id, listName }) => ({
    url: `${BASE_URL}/from-order/${id}`,
    method: "POST",
    data: { listName }
  }),

  GET_DROP_LISTS: ({ dropId, params }) => ({
    url: `${BASE_URL}/branch/${dropId}`,
    method: "GET",
    params
  }),

  GET_USER_ACCESSIBLE_LISTS: ({ params }) => ({
    url: `${BASE_URL}/user-accessible/all`,
    method: "GET",
    params
  }),

  GET_LIST: ({ listId }) => ({
    url: `${BASE_URL}/${listId}`,
    method: "GET"
  }),

  GET_LISTS: ({ ...params }) => ({
    url: `${BASE_URL}`,
    method: "GET",
    params
  }),

  PRE_UPDATE_LIST: ({ listId }) => ({
    url: `${BASE_URL}/pre-update/info`,
    method: "GET",
    params: { listId }
  }),

  CREATE_LIST: ({ ...data }) => ({
    url: `${BASE_URL}/`,
    method: "POST",
    data
  }),

  UPDATE_LIST: ({ ...data }) => ({
    url: `${BASE_URL}`,
    method: "PATCH",
    data
  }),

  DELETE_LIST: ({ listId }) => ({
    url: `${BASE_URL}/${listId}`,
    method: "DELETE"
  })
};

export const useListsApi: UseApiRequest<ListsEndpointConfig> = name =>
  useAxios(requests[ name ], name);
